import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, FormGroup } from 'react-bootstrap';
import { set, get, concat, cloneDeep, isArray } from 'lodash';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';

import { useSelector, useDispatch } from 'react-redux';
import {
  productListLocationsArray,
  customStyles,
  productListSubLocationsArray,
  checValidMongoId,
  customSectionPlacementArry,
  isExactMatch,
  PIMDisplayFormat,
  RightsAccess,
  productListDesignDetailOutComeList,
  productListDesignDetailAttributeList,
} from '../../../../Components/helper';
import { useCustomColumnState } from '../CustomColumnContextProvider';
import { sourcePath, errorpath } from '../fieldPath';
import * as Action from '../../../SystemMasters/Store/SystemMasters.action';
import { useCostingAccess } from '../../../../Components/CustomHooks/useCostingAccess';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';

function SourceComponent({ data, pIndex }) {
  const { customColumnState, setCustomColumnState, globalError, converDropdownData, ecomApiCall } = useCustomColumnState();
  const reduxData = useSelector((prevState) => prevState);
  const dispatch = useDispatch();
  const {
    customSectionData: { dynamicColArr, measurementArr, sampleTrackingDates, currSectionList, bomTypes },
    SystemMastersDetails: { mainSampleList, otherSamplesList, placement_master, measurement_master },
    UserDetails: { userInfo, isCompanyAdminLoggedIn },
    Users: { logInRights },
  } = reduxData;
  const { t } = useTranslation('common');

  const { TestingRight } = RightsAccess();
  const loggedInUserId = userInfo.userId;

  const hasCostingAccess = useCostingAccess();
  const { CheckModulePermission } = useComponent();
  const productIdentifierAccess = CheckModulePermission(logInRights, 'rangePlan', 'productIdentifier', isCompanyAdminLoggedIn);

  /**
   * Locations offered in the Location dropdown: the product-list locations minus the ones the
   * company or user has no rights to, followed by the custom sections. Derived rather than stored so
   * it follows rights that finish loading after the editor opens.
   */
  const locationArray = useMemo(() => {
    const hiddenLocations = [
      ...(TestingRight ? [] : ['Testing_Information']),
      ...(hasCostingAccess ? [] : ['Costing_Information']),
      ...(productIdentifierAccess ? [] : ['Product_Identifier']),
    ];
    const sectionLocations = isArray(currSectionList) ? currSectionList.map((section) => ({ label: section?.name, name: section?._id })) : [];
    return [...(productListLocationsArray || []).filter((item) => !hiddenLocations.includes(item.name)), ...sectionLocations];
  }, [TestingRight, hasCostingAccess, productIdentifierAccess, currSectionList]);

  const handleChange = (value, path) => {
    const copyState = customColumnState;
    if (path?.includes('subLocation') && get(copyState, sourcePath(pIndex, 'location').SOURCE_KEY_PATH)?._id === 4) {
      ecomApiCall();
    }
    set(copyState, path, value);
    if (isExactMatch(path, 'location')) {
      set(copyState, sourcePath(pIndex, 'subLocation').SOURCE_KEY_PATH, null);
      set(copyState, sourcePath(pIndex, 'locationType').SOURCE_KEY_PATH, null);
      set(copyState, sourcePath(pIndex, 'locationPlacement').SOURCE_KEY_PATH, null);
      set(copyState, sourcePath(pIndex, 'attribute').SOURCE_KEY_PATH, null);
      set(copyState, sourcePath(pIndex, 'output').SOURCE_KEY_PATH, null);
    }
    if (isExactMatch(path, 'attribute')) {
      set(copyState, sourcePath(pIndex, 'output').SOURCE_KEY_PATH, null);
    }
    setCustomColumnState((prevState) => ({ ...prevState, ...copyState }));
  };
  const setSubLocationArr = () => {
    let subLocationArr = cloneDeep(productListSubLocationsArray?.find((val) => val?.locationId === data?.source?.location?._id) || {});
    if (!subLocationArr?.subLocations) {
      const findLocation = productListLocationsArray.find((loc) => loc?.name === data?.source?.location?.name);
      subLocationArr = cloneDeep(productListSubLocationsArray?.find((val) => val?.locationId === findLocation?._id) || {});
    }
    let manipulatedDynamicColumns = converDropdownData(dynamicColArr);
    manipulatedDynamicColumns = manipulatedDynamicColumns?.map((dyCol) => {
      return {
        label: dyCol?.keyName,
        name: dyCol?._id,
        type: 'string',
      };
    });
    const manipulatedMeasurmentArr = measurementArr?.map((dyCol) => {
      return {
        label: dyCol?.name,
        name: dyCol?._id,
        type: 'string',
      };
    });
    const sampleTrackingArr = sampleTrackingDates?.map((dyCol) => {
      return {
        label: dyCol?.name,
        name: dyCol?._id,
        type: 'string',
      };
    });
    if (data?.source?.location?.name === 'Product_Information') {
      subLocationArr.subLocations = concat(subLocationArr?.subLocations, manipulatedDynamicColumns);
    }

    if (data?.source?.location?.name === 'Measurement_Information') {
      subLocationArr.subLocations = concat(subLocationArr?.subLocations, manipulatedMeasurmentArr);
    }
    if (data?.source?.location?.name === 'Sample_Information') {
      subLocationArr.subLocations = concat(subLocationArr?.subLocations, sampleTrackingArr);
    }
    if (data?.source?.location?.name && checValidMongoId(data?.source?.location?.name)) {
      const getSectionsColumns = cloneDeep(currSectionList?.find((val) => val?._id === data?.source?.location?.name));
      const subLocationObj = {};

      if (getSectionsColumns && isArray(getSectionsColumns?.columns) && getSectionsColumns?.columns.length > 0) {
        const filteredCol = getSectionsColumns?.columns.filter((col) => col.columnType !== 'calculation');
        const newSubColumns = [];
        for (const dyCol of filteredCol) {
          const obj = {
            label: dyCol?.title,
          };
          if (dyCol?.columnType === 'source') {
            obj.name = dyCol?._id;
            obj.type = dyCol?.source?.subLocation?.type;
          }
          if (dyCol?.columnType === 'manual') {
            obj.name = dyCol?._id;
            obj.type = 'manualNumber';
            obj.numberType = dyCol?.key;
          }
          newSubColumns.push(obj);
        }
        subLocationObj.subLocations = newSubColumns;
      }
      subLocationArr = subLocationObj;
      return subLocationArr;
    }
    return subLocationArr;
  };

  const setTypesForLocation = () => {
    if ([3].includes(data?.source?.location?._id) || ['Bill_of_Materials'].includes(data?.source?.location?.name)) {
      return bomTypes.map((val) => {
        return { name: val?._id, label: val?.name };
      });
    }
    if ([8].includes(data?.source?.location?._id) || ['Sample_Information'].includes(data?.source?.location?.name)) {
      let sampleTypes = concat(mainSampleList, otherSamplesList);
      sampleTypes = sampleTypes.map((val) => {
        return { name: val?._id, label: val?.name };
      });
      return sampleTypes;
    }
  };

  const setPlacementForLocation = () => {
    if (['Bill_of_Materials'].includes(data?.source?.location?.name)) {
      return placement_master.map((val) => {
        return { name: val?._id, label: val?.name };
      });
    }
    if (['Purchase_Orders', 'Sample_Information'].includes(data?.source?.location?.name)) {
      return customSectionPlacementArry;
    }
  };
  const subLocationOptions = useMemo(() => {
    return setSubLocationArr();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.source?.location?.name]);

  const subType = useMemo(() => {
    return setTypesForLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.source?.location?.name]);

  const subPlacement = useMemo(() => {
    return setPlacementForLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.source?.location?.name]);
  const hideShowType = ['Bill_of_Materials', 'Sample_Information'].includes(data?.source?.location?.name);
  const hideShowplacement = ['Bill_of_Materials', 'Sample_Information', 'Purchase_Orders'].includes(data?.source?.location?.name);
  const placementLabel = data?.source?.location?.name === 'Sample_Information' ? 'Select Position' : 'Select Placement';
  const showEcomOptions = data?.source?.location?.name === 'Measurement_Information' && data?.source?.subLocation?.name === 'ecomSpec';

  useEffect(() => {
    const callApi = async () => {
      const msParams = {
        bomTypeId: data?.source?.locationType?.name,
        loggedInUserId,
      };
      await dispatch(Action.getPlacementDetails({ ...msParams, isActive: true }));
    };
    if (data?.source?.locationType?.name && data?.source?.location?.name === 'Bill_of_Materials') {
      callApi();
    }
  }, [data?.source?.locationType]);

  return (
    <Row>
      <Col lg="12">
        <FormGroup className="mb-4">
          <Select
            name="Location"
            options={locationArray || []}
            getOptionLabel={(option) => `${option.label}`}
            getOptionValue={(option) => `${option.name}`}
            value={data?.source?.location}
            className="common-select"
            classNamePrefix="common-select"
            styles={customStyles}
            placeholder={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.SOURCE_COMPONENTS.SELECT_LOCATION')}
            maxMenuHeight={120}
            onChange={(selected) => handleChange(selected, sourcePath(pIndex, 'location').SOURCE_KEY_PATH)}
          />
          <span className="error-message position-absolute">{get(globalError, errorpath(pIndex, undefined, 'location').SOURCE_ERROR, '')}</span>
        </FormGroup>
      </Col>
      {data?.source?.location?.name === 'Design_Details' && (
        <>
          <Col lg="12">
            <FormGroup className="mb-4">
              <div className="common-react-select">
                <p className="field-name">
                  Attribute <span className="asterisk">*</span>
                </p>
                <Select
                  name="Attribute"
                  options={productListDesignDetailAttributeList || []}
                  getOptionLabel={(option) => `${option.label}`}
                  getOptionValue={(option) => `${option.name}`}
                  value={data?.source?.attribute}
                  className="common-select"
                  classNamePrefix="common-select"
                  styles={customStyles}
                  maxMenuHeight={120}
                  placeholder="Select Attribute"
                  onChange={(selected) => handleChange(selected, sourcePath(pIndex, 'attribute').SOURCE_KEY_PATH)}
                />
                <span className="error-message position-absolute">{get(globalError, errorpath(pIndex, undefined, 'attribute').SOURCE_ERROR, '')}</span>
              </div>
            </FormGroup>
          </Col>
          {data?.source?.attribute?.name && (
            <Col lg="12">
              <FormGroup className="mb-4">
                <div className="common-react-select">
                  <p className="field-name">
                    Output <span className="asterisk">*</span>
                  </p>
                  <Select
                    name="Output"
                    options={productListDesignDetailOutComeList || []}
                    getOptionLabel={(option) => `${option.label}`}
                    getOptionValue={(option) => `${option.name}`}
                    value={data?.source?.output}
                    className="common-select"
                    classNamePrefix="common-select"
                    styles={customStyles}
                    maxMenuHeight={120}
                    placeholder="Select Output"
                    onChange={(selected) => handleChange(selected, sourcePath(pIndex, 'output').SOURCE_KEY_PATH)}
                  />
                  <span className="error-message position-absolute">{get(globalError, errorpath(pIndex, undefined, 'output').SOURCE_ERROR, '')}</span>
                </div>
              </FormGroup>
            </Col>
          )}
        </>
      )}
      {hideShowType && (
        <Col lg="12">
          <FormGroup className="mb-4">
            <Select
              name="locationType"
              options={subType || []}
              getOptionLabel={(option) => `${option.label}`}
              getOptionValue={(option) => `${option.name}`}
              value={data?.source?.locationType}
              className="common-select"
              classNamePrefix="common-select"
              styles={customStyles}
              maxMenuHeight={120}
              placeholder={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.SOURCE_COMPONENTS.SELECT_TYPE')}
              onChange={(selected) => handleChange(selected, sourcePath(pIndex, 'locationType').SOURCE_KEY_PATH)}
            />
            <span className="error-message position-absolute">{get(globalError, errorpath(pIndex, undefined, 'locationType').SOURCE_ERROR, '')}</span>
          </FormGroup>
        </Col>
      )}
      {hideShowplacement && (
        <Col lg="12">
          <FormGroup className="mb-4">
            <Select
              name="locationPlacement"
              options={subPlacement || []}
              getOptionLabel={(option) => `${option.label}`}
              getOptionValue={(option) => `${option.name}`}
              value={data?.source?.locationPlacement}
              styles={customStyles}
              className="common-select"
              classNamePrefix="common-select"
              maxMenuHeight={120}
              placeholder={placementLabel}
              onChange={(selected) => handleChange(selected, sourcePath(pIndex, 'locationPlacement').SOURCE_KEY_PATH)}
            />
            <span className="error-message position-absolute">{get(globalError, errorpath(pIndex, undefined, 'locationPlacement').SOURCE_ERROR, '')}</span>
          </FormGroup>
        </Col>
      )}
      {data?.source?.location?.name !== 'Design_Details' && (
        <Col lg="12">
          <FormGroup className="mb-4">
            <Select
              name="SubLocation"
              options={subLocationOptions?.subLocations || []}
              getOptionLabel={(option) => `${option.label}`}
              getOptionValue={(option) => `${option.name}`}
              value={data?.source?.subLocation}
              styles={customStyles}
              className="common-select"
              classNamePrefix="common-select"
              maxMenuHeight={120}
              placeholder={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.SOURCE_COMPONENTS.SELECT_SUB_LOCATION')}
              onChange={(selected) => handleChange(selected, sourcePath(pIndex, 'subLocation').SOURCE_KEY_PATH)}
            />
            <span className="error-message position-absolute">{get(globalError, errorpath(pIndex, undefined, 'subLocation').SOURCE_ERROR, '')}</span>
          </FormGroup>
        </Col>
      )}
      {showEcomOptions && (
        <Col lg="12">
          <FormGroup className="mb-4">
            <Select
              name="PointOfMeasure"
              options={measurement_master?.map((e) => ({ label: e?.name, name: e?._id })) || []}
              getOptionLabel={(option) => `${option.label}`}
              getOptionValue={(option) => `${option.name}`}
              value={data?.source?.locationType}
              styles={customStyles}
              className="common-select"
              classNamePrefix="common-select"
              maxMenuHeight={120}
              placeholder={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.SOURCE_COMPONENTS.PIONT_OF_MEASURE')}
              onChange={(selected) => handleChange(selected, sourcePath(pIndex, 'locationType').SOURCE_KEY_PATH)}
            />
            <span className="error-message position-absolute">{get(globalError, errorpath(pIndex, undefined, 'locationType').SOURCE_ERROR, '')}</span>
          </FormGroup>
        </Col>
      )}
      {showEcomOptions && (
        <Col lg="12">
          <FormGroup className="mb-4">
            <Select
              name="DisplayFormat"
              options={PIMDisplayFormat || []}
              getOptionLabel={(option) => `${option.label}`}
              getOptionValue={(option) => `${option.name}`}
              value={data?.source?.locationPlacement}
              styles={customStyles}
              className="common-select"
              classNamePrefix="common-select"
              maxMenuHeight={120}
              placeholder={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.SOURCE_COMPONENTS.DISPLAY_FORMAT')}
              onChange={(selected) => handleChange(selected, sourcePath(pIndex, 'locationPlacement').SOURCE_KEY_PATH)}
            />
            <span className="error-message position-absolute">{get(globalError, errorpath(pIndex, undefined, 'locationPlacement').SOURCE_ERROR, '')}</span>
          </FormGroup>
        </Col>
      )}
    </Row>
  );
}

export default SourceComponent;
